import createHmac from 'create-hmac';

import { merge } from 'lodash-es';
import catchify from 'catchify';

import User from '../../../services/user';
import Production from '../../../services/productions';

import { setUserToken } from '../../../utils/request';

const state = {
  id: null,
  username: '',
  createdTime: null,
  auth: '',
  email: '',
  groups: [],
  displayName: '',
  profileImageUrl: '',
  token: '',
  composer: false,

  has_panels: false,
  permissions: {},
  accounts: {},
  previewVideoVolume: 1,

  // Does not necessarily mean the user cannot create productions completely.
  // This is only to track if the logged-in user is a team owner
  canCreateProductions: false,
};

/**
 * Get query params for current location.
 * @returns {Object} key/value map of query params
 */
const getQueryParams = () => {
  const { search } = window.location;

  if (!search) {
    return {};
  }

  return search.substr(1).split('&').reduce((memo, query) => {
    const [key, value] = query.split('=');

    return {
      ...memo,
      [key]: value || true, // Fallback to true if query param doesn't have a value set
    };
  }, {});
};

/**
 * Triming starting / or /c/ from path and retrieves user ID.
 * This is used by staff for impersonation. If we ever expand the paths for producer.tagboard.com,
 * then this will have to change.
 * @returns {Number|null}
 */
const getUserIdFromPath = () => {
  const { pathname: path } = window.location;

  // First check if user_id is provided as query param
  const query = getQueryParams();
  if (query?.user_id) {
    return parseInt(query?.user_id, 10) || null;
  }

  // Check route param second (e.g. /1006 or /c/1006)
  return parseInt(
    path.replace(/^\/c\//, '').replace(/^\//, ''),
    10,
  ) || null;
};

const actions = {
  /**
   * Get info for current logged in use to populate state
   * If current user is staff, and URL contains an ID to impersonate,
   * will follow up by loading that and storing that user instead.
   * @returns {Promise<Boolean>} True if user was fetched and is now authorized.
   */
  async getUserInfo(context) {
    // Grab userId to impersonate from path name
    // We'll use this to fetch info if it's provided.
    // The endpoint will reject it if logged in user isn't staff so we don't need a staff check here.
    const userId = getUserIdFromPath();
    const [err, user] = await catchify(User.getInfo(userId));

    if (err) {
      if (err.status === 401) {
        // Go to login
        window.location = `${process.env.TAGBOARD_ACCOUNT_URL}/signin?return_url=${encodeURIComponent(window.location.href)}`;
        return false; // Early return stops the error from bubbling up to Sentry
      }

      window.Sentry?.captureException(err);

      // Bubble up error, so we don't try to continue into Producer still
      throw err;
    }

    context.commit('SET_USER', user);

    // Setup Heap
    if (window.heap) {
      window.heap.load(process.env.HEAP_API_KEY);
    }

    // Setup Segment
    if (window.analytics) {
      window.analytics.load(process.env.SEGMENT_API_KEY);
      window.analytics.page();
    }

    // Setup intercom with user info
    if (window.Intercom) {
      const hmac = createHmac('sha256', process.env.INTERCOM_HASH_SECRET);
      hmac.update(context.state.id);
      const hash = hmac.digest('hex');

      window.Intercom('boot', {
        app_id: process.env.INTERCOM_APP_ID,
        user_hash: hash,
        name: context.state.displayName,
        email: context.state.email,
        user_id: context.state.id,
        hide_default_launcher: true,
        created_at: context.state.createdTime,
      });
    }

    return true;
  },

  async getUserAccounts(context) {
    const accounts = await User.getUserAccounts(state.id);
    context.commit('SET_USER_ACCOUNTS', accounts);
    return accounts;
  },

  /**
   * Specifically check permissions for the signed-in user, disregarding any teams they are on.
   * This is needed so we know if they user has permission to create productions outside of any team owner.
   */
  async getUserPermissions(context) {
    const resp = await Production.getUserPermissions(context.state.id);
    context.commit('SET_USER_PERMISSIONS', resp);
    return resp;
  },

  async getUserComposerAccounts(context) {
    const accounts = await User.getUserAccounts(state.id, 'composer');
    context.commit('SET_USER_ACCOUNTS', accounts);
    return accounts;
  },

  async getPermissions(context) {
    const { production } = context.rootState.production;

    const permissions = await User.getPermissions(production.owner, [
      'qr_code_analytics',
      'panels',
    ]);

    context.commit('SET_PERMISSIONS', permissions);
    return permissions;
  },

  /**
   * Generic function for one-off sets to state properties
   */
  setUserState(context, payload) {
    context.commit('SET_USER_STATE', payload);
  },
};

const mutations = {
  /* eslint-disable no-shadow, no-param-reassign */

  SET_USER_STATE(state, { key, value }) {
    state[key] = value;
  },

  SET_USER(state, user = {}) {
    const { profile, email } = user;

    state.id = user.id;
    state.groups = user.groups;
    state.createdTime = user.created_time;
    state.token = user.token;

    // Set token in request lib, so header is added to requests
    setUserToken(user.token);

    state.email = email?.address || profile?.email;

    state.displayName = profile?.displayName;
    state.auth = profile?.network;
    state.username = profile?.username;
    state.profileImageUrl = profile?.profileImageUrl;
  },

  SET_USER_ACCOUNTS(state, payload) {
    merge(state.accounts, payload);
  },

  SET_USER_PERMISSIONS(state, payload) {
    state.canCreateProductions = payload.canCreateProductions;
  },

  SET_PERMISSIONS(state, permissions) {
    state.permissions = permissions;

    if ('panels' in permissions) {
      state.has_panels = !!permissions.panels;
    }
  },

  SET_PREVIEW_VIDEO_VOLUME(state, volume) {
    state.previewVideoVolume = volume;
  },
};

const getters = {
  /**
   * Returns true if user is on more than one team
   * @returns {Boolean}
   */
  hasTeams(state) {
    const { owners } = state.accounts || {};

    const teams = [
      state.canCreateProductions ? 1 : 0,
      ...Object.keys(owners || {}),
    ].filter(Boolean);

    return teams.length > 1;
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
